// "About this fund" (DQ-15): the cited asset profile a class board entry
// carries, turned into labelled rows for the token panel. Every fact comes
// with the official page it was read from; a field the registry does not
// give is left out, never filled.

import { compactUsd } from "./format";

export interface SourcedFact<T = string | number> {
  value: T;
  source_url: string;
}

/** The class board's `profile`: every field, null when the registry does not give it. */
export interface BoardProfile {
  isin: SourcedFact<string> | null;
  cusip: SourcedFact<string> | null;
  inception_date: SourcedFact<string> | null;
  management_fee_bps: SourcedFact<number> | null;
  min_investment_usd: SourcedFact<number> | null;
  eligibility: SourcedFact<string> | null;
  jurisdiction: SourcedFact<string> | null;
  custodian: SourcedFact<string> | null;
  administrator: SourcedFact<string> | null;
  auditor: SourcedFact<string> | null;
  redemption: SourcedFact<string> | null;
  share_class_currency: SourcedFact<string> | null;
}

export type ProfileKey = keyof BoardProfile;

export interface ProfileRow {
  key: ProfileKey;
  label: string;
  value: string;
  source_url: string;
}

const ELIGIBILITY: Record<string, string> = {
  retail: "Retail investors",
  accredited: "Accredited investors",
  qualified_purchaser: "Qualified purchasers",
  non_us_only: "Non-US investors only",
  institutional: "Institutional / professional investors",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const FIELDS: ReadonlyArray<readonly [ProfileKey, string, (value: string | number) => string]> = [
  ["jurisdiction", "Domicile", String],
  ["eligibility", "Who can invest", (v) => ELIGIBILITY[String(v)] ?? String(v)],
  ["min_investment_usd", "Minimum investment", (v) => (Number(v) === 0 ? "No minimum" : minimum(Number(v)))],
  ["management_fee_bps", "Management fee", (v) => `${(Number(v) / 100).toFixed(2).replace(/\.?0+$/, "")}% a year (${v} bps)`],
  ["redemption", "Redemption", String],
  ["share_class_currency", "Share class currency", String],
  ["inception_date", "Inception", (v) => dateText(String(v))],
  ["custodian", "Custodian", String],
  ["administrator", "Administrator", String],
  ["auditor", "Auditor", String],
  ["isin", "ISIN", String],
  ["cusip", "CUSIP", String],
];

/** "$5,000,000" up to ten million, "$25M" beyond: a minimum reads best in full. */
function minimum(usd: number): string {
  return usd < 10_000_000 ? `$${Math.round(usd).toLocaleString("en-US")}` : compactUsd(usd);
}

/** "Oct 6, 2026" from "2026-10-06"; anything else as given. */
function dateText(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : date;
}

/** The profile's rows, in reading order (who and how first, identifiers last), stated fields only. */
export function profileRows(profile: Partial<BoardProfile> | null | undefined): ProfileRow[] {
  if (!profile) return [];
  return FIELDS.flatMap(([key, label, format]) => {
    const fact = profile[key];
    if (!fact || fact.value === null || fact.value === undefined || !fact.source_url) return [];
    return [{ key, label, value: format(fact.value), source_url: fact.source_url }];
  });
}

/** "fund page", "factsheet", "sec.gov": a short name for a source link, from its address. */
export function sourceLabel(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (/\.pdf($|\?)/i.test(u.pathname)) return `${host} (PDF)`;
    return host;
  } catch {
    return "source";
  }
}
